import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import JsBarcode from "jsbarcode";
import { Search, X, QrCode, FileSearch, Printer, Info, CheckCheck } from "lucide-react";
import { Wrench, ShieldCheck, Shield, ShieldPlus, AlertTriangle, type LucideIcon } from "lucide-react";
import { api, ApiClientError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useToast } from "../lib/toast";
import { StepLabel, OptionCard } from "../components/StepLabel";
import type { GrcPricingRow } from "@sbd-tcic/shared-types";

interface CustomerRow {
  id: string;
  customerNumber: string;
  name: string;
  type: string;
  status: string;
  address: string;
  phone: string;
  email: string;
  contact: string;
}

const RO_TYPES: { key: string; icon: LucideIcon; serviceType: string }[] = [
  { key: "Repair Order", icon: Wrench, serviceType: "GRC (Guaranteed Repair Cost)" },
  { key: "Warranty Exchange", icon: ShieldCheck, serviceType: "Warranty Exchange" },
  { key: "Service Warranty", icon: Shield, serviceType: "Service Warranty" },
  { key: "Extended Warranty", icon: ShieldPlus, serviceType: "Extended Warranty" },
  { key: "Recall", icon: AlertTriangle, serviceType: "Recall" },
];
const SERVICE_TYPES = ["GRC (Guaranteed Repair Cost)", "Warranty", "Extended Warranty", "Service Warranty", "Time & Material", "Recall"];
const ORDER_METHODS = ["Walk-In", "Phone", "Email", "FSC (Pickup)"];
const NOTIFICATION_TYPES = ["Email", "SMS / Text", "Email + SMS"];
const FREIGHT_METHODS = ["UPS Ground", "FedEx Ground", "Local Courier"];
const DEFAULT_GRC_PRICE = 79.0;
const money = (n: number) => `$${n.toFixed(2)}`;

function CustomerSearchField({ label, customer, onPick, onClear }: { label: string; customer: CustomerRow | null; onPick: (c: CustomerRow) => void; onClear: () => void }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CustomerRow[]>([]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!query.trim()) return setResults([]);
      void api.get<CustomerRow[]>(`/customers?query=${encodeURIComponent(query)}`).then(setResults);
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  return (
    <div>
      <label className="field-label">{label}</label>
      {customer ? (
        <div className="card p-3.5 border-[color:#cfe3ff]">
          <div className="flex items-start justify-between">
            <div>
              <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold bg-status-green-bg text-status-green">{customer.status}</span>
              <div className="font-bold text-sm mt-1.5">{customer.name}</div>
              <div className="text-xs text-ink-muted">
                {customer.customerNumber} · {customer.type}
              </div>
            </div>
            <button aria-label="Clear" className="text-ink-muted hover:text-ink" onClick={onClear}>
              <X size={14} />
            </button>
          </div>
          <div className="border-t border-border my-2.5" />
          <div className="text-xs leading-relaxed">
            <div>{customer.address}</div>
            <div>{customer.phone}</div>
            <div>{customer.email}</div>
            <div>{customer.contact}</div>
          </div>
        </div>
      ) : (
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input className="input pl-8" placeholder="Search by Customer # or Name" value={query} onChange={(e) => setQuery(e.target.value)} autoComplete="off" />
          {results.length > 0 && (
            <div className="absolute z-10 mt-1 w-full card shadow-pop max-h-56 overflow-y-auto">
              {results.map((c) => (
                <button
                  key={c.id}
                  className="w-full text-left px-3.5 py-2.5 hover:bg-surface border-b border-border last:border-0"
                  onClick={() => {
                    onPick(c);
                    setResults([]);
                    setQuery(c.name);
                  }}
                >
                  <div className="font-semibold text-sm">{c.name}</div>
                  <div className="text-xs text-ink-muted">
                    {c.customerNumber} · {c.address}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function RepairOrderNew() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  const [roType, setRoType] = useState(RO_TYPES[0]!.key);
  const [orderMethod, setOrderMethod] = useState(ORDER_METHODS[0]);
  const [notificationType, setNotificationType] = useState(NOTIFICATION_TYPES[0]);
  const [soldTo, setSoldTo] = useState<CustomerRow | null>(null);
  const [shipTo, setShipTo] = useState<CustomerRow | null>(null);

  const [modelNumber, setModelNumber] = useState("");
  const [dateCode, setDateCode] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [typeNumber, setTypeNumber] = useState("1");
  const [serviceType, setServiceType] = useState<string>(SERVICE_TYPES[0]!);
  const [toolFailure, setToolFailure] = useState("");
  const [priority, setPriority] = useState<"FIFO" | "Expedited" | "VIP">("FIFO");

  const [deliveryMethod, setDeliveryMethod] = useState<"Ship Out to Customer" | "Customer Pickup">("Ship Out to Customer");
  const [freightMethod, setFreightMethod] = useState(FREIGHT_METHODS[0]);
  const [orderNotes, setOrderNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");

  const [grcPricing, setGrcPricing] = useState<GrcPricingRow[]>([]);
  const [nextId, setNextId] = useState("—");
  const [busy, setBusy] = useState(false);
  const barcodeRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    void api.get<GrcPricingRow[]>("/grc-pricing").then(setGrcPricing);
    void api.get<{ id: string }>("/repair-orders/next-id").then((r) => setNextId(r.id));
  }, []);

  const grcPrice = useMemo(() => {
    if (!serviceType.startsWith("GRC")) return { price: 0, isPlaceholder: false };
    const row = grcPricing.find((g) => g.modelNumber.toUpperCase() === modelNumber.trim().toUpperCase());
    return row ? { price: row.price, isPlaceholder: row.isPlaceholder } : { price: DEFAULT_GRC_PRICE, isPlaceholder: true };
  }, [grcPricing, modelNumber, serviceType]);

  useEffect(() => {
    if (!barcodeRef.current) return;
    try {
      JsBarcode(barcodeRef.current, nextId, { format: "CODE128", height: 40, displayValue: false, margin: 0 });
    } catch {
      // ignore — an empty/placeholder id before the preview loads isn't barcode-encodable.
    }
  }, [nextId]);

  const orderDate = new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
  const canSubmit = !!soldTo && modelNumber.trim() && serialNumber.trim();

  async function submit() {
    if (!soldTo) return;
    setBusy(true);
    try {
      const result = await api.post<{ id: string; grcPrice: number; grcIsPlaceholder: boolean }>("/repair-orders", {
        customerId: soldTo.id,
        shipToId: shipTo?.id ?? soldTo.id,
        equipment: modelNumber.trim(),
        modelNumber: modelNumber.trim(),
        dateCode: dateCode.trim(),
        serialNumber: serialNumber.trim(),
        typeNumber: typeNumber.trim(),
        serviceType,
        toolFailure: toolFailure.trim(),
        priority,
        deliveryMethod,
        freightMethod: deliveryMethod === "Ship Out to Customer" ? freightMethod : undefined,
        freightRate: deliveryMethod === "Ship Out to Customer" ? 14.25 : undefined,
        orderMethod,
        orderNotes: orderNotes.trim() || undefined,
        internalNotes: internalNotes.trim() || undefined,
      });
      toast(`Repair order ${result.id} created${result.grcIsPlaceholder ? " (GRC price is a placeholder — confirm before invoicing)" : ""}.`, result.grcIsPlaceholder ? "info" : "success");
      navigate(`/repair-orders/${result.id}`);
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to create the repair order.", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="card p-4 mb-4 grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr] gap-5">
        <div>
          <StepLabel step={1}>Order Type</StepLabel>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {RO_TYPES.map((t) => (
              <OptionCard
                key={t.key}
                icon={t.icon}
                label={t.key}
                selected={t.key === roType}
                onClick={() => {
                  setRoType(t.key);
                  setServiceType(t.serviceType);
                }}
              />
            ))}
          </div>
        </div>
        <div>
          <StepLabel step={2}>Order Method</StepLabel>
          <select className="input" value={orderMethod} onChange={(e) => setOrderMethod(e.target.value)}>
            {ORDER_METHODS.map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </div>
        <div>
          <StepLabel step={3}>Notification Type</StepLabel>
          <select className="input" value={notificationType} onChange={(e) => setNotificationType(e.target.value)}>
            {NOTIFICATION_TYPES.map((n) => (
              <option key={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2.2fr_1fr] gap-4 items-start">
        <div className="flex flex-col gap-4">
          <div className="card p-4">
            <StepLabel step={4}>Customer Information</StepLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CustomerSearchField label="Sold To (Customer)" customer={soldTo} onPick={setSoldTo} onClear={() => setSoldTo(null)} />
              <CustomerSearchField label="Ship To (Deliver To)" customer={shipTo} onPick={setShipTo} onClear={() => setShipTo(null)} />
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <StepLabel step={5}>
                <span>Tool Information</span>
              </StepLabel>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  className="btn-outline"
                  onClick={() => {
                    // No camera/QR-hardware integration exists in this build (matching POS's
                    // identically-labeled "Scan Barcode" simulation) — so this stays an honestly
                    // disclosed simulation, but a real one: it now actually populates the fields
                    // it claims to, picking a real catalog model number (from the same real
                    // `/grc-pricing` data this screen already loads for the price badge) rather
                    // than inventing one, with a plausible example date code/serial for the two
                    // fields no real per-unit registry exists for.
                    if (grcPricing.length === 0) {
                      toast("Model catalog hasn't loaded yet — try again in a moment.", "error");
                      return;
                    }
                    const picked = grcPricing[Math.floor(Math.random() * grcPricing.length)]!;
                    const scannedDateCode = `${2024 + Math.floor(Math.random() * 2)}-${String(1 + Math.floor(Math.random() * 12)).padStart(2, "0")}`;
                    const scannedSerial = `SN-${Math.floor(10000 + Math.random() * 90000)}`;
                    setModelNumber(picked.modelNumber);
                    setDateCode(scannedDateCode);
                    setSerialNumber(scannedSerial);
                    toast(`Simulated QR scan — populated ${picked.modelNumber}, date code ${scannedDateCode}, serial ${scannedSerial}.`, "info");
                  }}
                >
                  <QrCode size={15} /> Scan QR Code
                </button>
                <button className="btn-outline" onClick={() => toast("Registration lookup simulated.", "info")}>
                  <FileSearch size={15} /> Lookup Registration
                </button>
                <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-bold bg-[#fff6dd] text-[#8a6d1d]">
                  GRC (Guaranteed Repair Cost) — {money(grcPrice.price)}
                  {grcPrice.isPlaceholder && grcPrice.price > 0 && <span className="ml-1 opacity-70">(placeholder)</span>}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <div>
                <label className="field-label">Model Number</label>
                <input className="input" value={modelNumber} onChange={(e) => setModelNumber(e.target.value)} placeholder="DCF887B" />
              </div>
              <div>
                <label className="field-label">Date Code</label>
                <input className="input" value={dateCode} onChange={(e) => setDateCode(e.target.value)} placeholder="202312" />
              </div>
              <div>
                <label className="field-label">Serial Number</label>
                <input className="input" value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} placeholder="DW123456789" />
              </div>
              <div>
                <label className="field-label">Type Number (Tool Iteration)</label>
                <input className="input" value={typeNumber} onChange={(e) => setTypeNumber(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="field-label">Service Type</label>
                <select className="input" value={serviceType} onChange={(e) => setServiceType(e.target.value)}>
                  {SERVICE_TYPES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label">Tool Failure</label>
                <input className="input" value={toolFailure} onChange={(e) => setToolFailure(e.target.value)} placeholder="Motor Not Working" />
              </div>
              <div>
                <label className="field-label">Priority</label>
                <select className="input" value={priority} onChange={(e) => setPriority(e.target.value as typeof priority)}>
                  <option>FIFO</option>
                  <option>Expedited</option>
                  <option>VIP</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="field-label">Manufacturing Status</label>
                <div className="pt-1.5">
                  <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold bg-status-green-bg text-status-green">● Live</span>{" "}
                  <span className="text-xs text-ink-muted">Since 01/01/2022</span>
                </div>
              </div>
              <div>
                <label className="field-label">GSIS Recommendation</label>
                <div className="pt-1.5">
                  <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold bg-status-blue-bg text-brand-blue-dark">Repair the Tool</span>{" "}
                  <span className="text-xs text-ink-muted">Last Updated 05/10/2024</span>
                </div>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <StepLabel step={6}>Return Delivery (Ship Tool Back to Customer)</StepLabel>
            <p className="text-xs text-ink-muted mb-2.5">Customer is bringing the tool into the service center. Choose how the tool will be returned.</p>
            <div className="flex gap-5 mb-3 flex-wrap">
              <label className="flex items-center gap-1.5 text-[13px] font-semibold">
                <input type="radio" name="ron-delivery" checked={deliveryMethod === "Ship Out to Customer"} onChange={() => setDeliveryMethod("Ship Out to Customer")} />
                Ship Out to Customer
              </label>
              <label className="flex items-center gap-1.5 text-[13px] font-semibold">
                <input type="radio" name="ron-delivery" checked={deliveryMethod === "Customer Pickup"} onChange={() => setDeliveryMethod("Customer Pickup")} />
                Customer Pickup
              </label>
            </div>
            {deliveryMethod === "Ship Out to Customer" && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-2.5">
                <div>
                  <label className="field-label">Freight Method</label>
                  <select className="input" value={freightMethod} onChange={(e) => setFreightMethod(e.target.value)}>
                    {FREIGHT_METHODS.map((f) => (
                      <option key={f}>{f}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="field-label">Freight Rate</label>
                  <input className="input" value="$14.25" readOnly />
                </div>
                <div>
                  <label className="field-label">Estimated Transit</label>
                  <input className="input" value="2 - 3 Business Days" readOnly />
                </div>
              </div>
            )}
            <div className="rounded-md bg-status-blue-bg text-brand-blue-dark text-[12.5px] px-3 py-2.5 mb-3.5 flex items-center gap-1.5">
              <Info size={14} className="flex-shrink-0" />
              Shipping charges will be added to the final invoice at time of repair completion.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="field-label">Order Notes</label>
                <textarea className="input" rows={2} maxLength={250} placeholder="Add any additional notes about this repair..." value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)} />
              </div>
              <div>
                <label className="field-label">Internal Notes (Not Printed on Tag)</label>
                <textarea className="input" rows={2} maxLength={250} placeholder="Internal notes for the repair team..." value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4 sticky top-20">
          <div className="card p-4">
            <div className="font-bold text-sm mb-2.5">Order Summary</div>
            <div className="text-[13px]">
              {[
                ["Order Number (Auto)", nextId],
                ["Order Date", orderDate],
                ["Order Type", roType],
                ["Order Method", orderMethod],
                ["Customer", soldTo ? soldTo.name : "—"],
              ].map(([l, v]) => (
                <div key={l} className="flex justify-between py-1">
                  <span className="text-ink-muted">{l}</span>
                  <span>{v}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-border my-2.5" />
            <div className="text-xs rounded-md bg-surface px-2.5 py-2.5 text-ink-muted">Collected at Repair Completion. No payment is collected at time of write-up.</div>
          </div>

          <div className="card p-4">
            <div className="font-bold text-sm mb-2.5">Tool Tag / Label Preview</div>
            <div className="border-[1.5px] border-dashed border-border rounded-[10px] p-3.5 text-center">
              <div className="text-xs font-bold tracking-wide">SERVICE CENTER {user?.serviceCenterId ?? "—"}</div>
              <div className="text-xs text-ink-muted">REPAIR ORDER</div>
              <div className="text-[22px] font-extrabold my-1.5">{nextId}</div>
              <svg ref={barcodeRef} className="w-full" />
              <div className="text-xs text-ink-muted">Scan to view order details</div>
              <div className="border-t border-border my-2.5" />
              <div className="text-left text-[12.5px]">
                {soldTo ? (
                  <>
                    <strong>{soldTo.name}</strong>
                    <br />
                    {soldTo.phone}
                  </>
                ) : (
                  <span className="text-ink-muted">Select a customer to preview tag</span>
                )}
              </div>
            </div>
            <button className="btn-outline w-full justify-center mt-3" onClick={() => toast("Tool tag sent to printer.", "success")}>
              <Printer size={15} /> Print Tool Tag
            </button>
          </div>

          <button className="btn-primary w-full justify-center" disabled={!canSubmit || busy} onClick={() => void submit()}>
            <CheckCheck size={16} /> {busy ? "Creating…" : "Create Repair Order"}
          </button>
        </div>
      </div>
    </div>
  );
}
